import React, { useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import {
  ModalOverlay,
  ModalBox,
  ModalTitle,
  ModalSubtitle,
  ModalLabel,
  ModalSelect,
  ModalFooter,
  ModalCancelButton,
  ModalSaveButton,
  StatusMessage,
} from '../verification.styles';
import { useAcceptableDocuments, useUpdateVerificationResult } from '../hook/Useverification';

/*
  Props:
   - applicationId (= listId)
   - submission: the current card's raw object from getVerificationData
       ({ _id (submissionId), name, documentId, ... })
   - onClose()
   - onSaved() — called after a successful save so the parent can refetch
*/

const EditVerificationModal = ({ applicationId, submission, onClose, onSaved }) => {
  const { data, isLoading, isError, error } = useAcceptableDocuments(applicationId);
  const options = data?.data ?? [];

  const [selectedPdfId, setSelectedPdfId] = useState('');

  const updateMutation = useUpdateVerificationResult({
    onSuccess: () => {
      onSaved?.();
      onClose?.();
    },
  });

  const handleSave = () => {
    if (!selectedPdfId) return;
    updateMutation.mutate({
      applicationId,
      submissionId: submission._id,
      pdfId: selectedPdfId,
    });
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalTitle>{submission?.name}</ModalTitle>
        <ModalSubtitle>Choose the correct document type for this file.</ModalSubtitle>

        <ModalLabel htmlFor="acceptable-doc-select">Document type</ModalLabel>
        {isLoading ? (
          <StatusMessage>
            <Loader2 size={14} className="spin" /> Loading options…
          </StatusMessage>
        ) : isError ? (
          <StatusMessage $type="error">
            <AlertCircle size={14} />
            {error?.message || 'Failed to load document options.'}
          </StatusMessage>
        ) : (
          <ModalSelect
            id="acceptable-doc-select"
            value={selectedPdfId}
            onChange={(e) => setSelectedPdfId(e.target.value)}
            disabled={updateMutation.isPending}
          >
            <option value="" disabled>
              Select a document type
            </option>
            {options
              .filter((opt) => opt._id) // drop the "UnKnown" / null placeholder from the picker
              .map((opt) => (
                <option key={opt._id} value={opt._id}>
                  {opt.name}
                </option>
              ))}
          </ModalSelect>
        )}

        {updateMutation.isError && (
          <StatusMessage $type="error">
            <AlertCircle size={14} />
            {updateMutation.error?.message || 'Failed to save. Please try again.'}
          </StatusMessage>
        )}

        <ModalFooter>
          <ModalCancelButton type="button" onClick={onClose} disabled={updateMutation.isPending}>
            Cancel
          </ModalCancelButton>
          <ModalSaveButton
            type="button"
            onClick={handleSave}
            disabled={!selectedPdfId || updateMutation.isPending}
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 size={14} className="spin" /> Saving…
              </>
            ) : (
              'Save'
            )}
          </ModalSaveButton>
        </ModalFooter>
      </ModalBox>
    </ModalOverlay>
  );
};

export default EditVerificationModal;
